import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Check,
  Sparkles,
  Users,
  ImagePlus,
  Mail,
  ArrowRight,
  X as XIcon,
  Rocket,
} from 'lucide-react';

/**
 * ActivationOverlay
 * ─────────────────
 * Additive UI/UX guidance layer on top of the existing onboarding system
 * (OnboardingTour + OnboardingChecklist + PostSignupChecklist). Does NOT
 * replace, modify, or duplicate any existing onboarding logic — it simply
 * READS the same `completed` flags + live counts already maintained by
 * AppLayout and renders a friendly non-blocking activation overlay that
 * focuses the user on the 3 highest-impact first actions:
 *
 *   1. Add a parent / first family member
 *   2. Upload first memory
 *   3. Invite at least one family member
 *
 * Behavior:
 *   • Opens on dashboard load if at least one step is incomplete.
 *   • Fully dismissible (Esc, click-outside, X). Never blocking.
 *   • Once dismissed, leaves a small "Continue Setup" pill in the corner
 *     so users can reopen any time.
 *   • Emphasizes the next incomplete step with a gradient ring; completed
 *     steps render with a green check.
 *   • Closes itself automatically the first time all three steps are done.
 *
 * Pure UI — no DB writes, no relationship logic, no schema changes, no
 * effect on uploads, billing, auth, Resend, or invites.
 */
export interface ActivationOverlayProps {
  /** Existing completed flags from AppLayout (localStorage-backed). */
  completed: { added: boolean; uploaded: boolean; invited: boolean };
  /** Live counts from the family graph — used to auto-derive activation
   *  status without changing existing checklist logic. */
  peopleCount: number;
  mediaCount: number;
  /** Called when the user taps "Add Family Member". */
  onAddMember: () => void;
  /** Called when the user taps "Upload Memory". */
  onUploadMemory: () => void;
  /** Called when the user taps "Invite Family". */
  onInviteFamily: () => void;
  /** Optional: called when the user picks "Take a tour" from the overlay. */
  onOpenTour?: () => void;
}

const STORAGE_KEY_DISMISSED = 'kc:activation:dismissed:session';

const ActivationOverlay: React.FC<ActivationOverlayProps> = ({
  completed,
  peopleCount,
  mediaCount,
  onAddMember,
  onUploadMemory,
  onInviteFamily,
  onOpenTour,
}) => {
  // Derive activation status from BOTH the existing flags AND live data
  // so the overlay reflects reality even if a user added members through
  // a path that didn't fire markComplete (defensive — no behavior change
  // to the existing checklist).
  const hasAddedFamilyMember = completed.added || peopleCount > 1;
  const hasUploadedMemory = completed.uploaded || mediaCount > 0;
  const hasInvitedFamily = completed.invited; // invite is a user action
  const allDone = hasAddedFamilyMember && hasUploadedMemory && hasInvitedFamily;

  // Per-session dismissal so the overlay doesn't pop up every refresh
  // once the user closed it. The persistent "Continue Setup" banner
  // remains available so users can always reopen.
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY_DISMISSED) === '1';
    } catch {
      return false;
    }
  });
  const [open, setOpen] = useState<boolean>(false);

  // ───────── Modal-priority coordination (UI-only) ─────────
  // The Premium plan modal (MembershipModal) takes priority over this
  // activation guide. While it is open we MUST suppress ourselves so the
  // user never sees stacked onboarding + premium overlays after sign-in.
  // MembershipContext dispatches `kc:premium-modal-open/close` whenever
  // its open state flips. Purely additive — does not touch billing,
  // Stripe, auth, or any onboarding logic.
  const [premiumOpen, setPremiumOpen] = useState<boolean>(() => {
    // Best-effort initial detection in case Premium modal is already
    // mounted when this overlay renders (e.g. post-signup redirect).
    try {
      return !!document.querySelector(
        '[data-radix-dialog-overlay][data-state="open"]',
      ) && false; // start false — we receive the explicit event below.
    } catch {
      return false;
    }
  });
  useEffect(() => {
    const onOpen = () => setPremiumOpen(true);
    const onClose = () => setPremiumOpen(false);
    window.addEventListener('kc:premium-modal-open', onOpen);
    window.addEventListener('kc:premium-modal-close', onClose);
    return () => {
      window.removeEventListener('kc:premium-modal-open', onOpen);
      window.removeEventListener('kc:premium-modal-close', onClose);
    };
  }, []);

  // The moment the premium modal opens, force this overlay closed. We
  // intentionally do NOT mark it dismissed — once the premium modal
  // closes, the normal auto-open logic below can show it again if the
  // user still has incomplete activation steps.
  useEffect(() => {
    if (premiumOpen && open) setOpen(false);
  }, [premiumOpen, open]);

  // Auto-open the overlay shortly after mount when there's still work to
  // do, the user hasn't dismissed it this session, AND the Premium plan
  // modal is not currently shown. Slight delay keeps it from competing
  // with the existing first-time tour or the post-signup checkout flow.
  useEffect(() => {
    if (allDone) return;
    if (dismissed) return;
    if (premiumOpen) return;
    const t = window.setTimeout(() => {
      // Re-check premiumOpen at fire time via a fresh closure read; if
      // the premium modal opened during the delay window we bail out.
      if (premiumOpen) return;
      setOpen(true);
    }, 1500);
    return () => window.clearTimeout(t);
  }, [allDone, dismissed, premiumOpen]);


  const close = () => {
    setOpen(false);
    setDismissed(true);
    try {
      sessionStorage.setItem(STORAGE_KEY_DISMISSED, '1');
    } catch {
      /* ignore */
    }
  };

  const reopen = () => {
    setDismissed(false);
    try {
      sessionStorage.removeItem(STORAGE_KEY_DISMISSED);
    } catch {
      /* ignore */
    }
    setOpen(true);
  };

  // Step config. Order matches the activation funnel.
  const steps = useMemo(
    () => [
      {
        key: 'added',
        title: 'Add a parent or family member',
        body: 'Start your tree with anyone — you can add in any order.',
        done: hasAddedFamilyMember,
        action: onAddMember,
        cta: 'Add Family Member',
        Icon: Users,
      },
      {
        key: 'uploaded',
        title: 'Upload your first memory',
        body: 'A photo or video brings your family timeline to life.',
        done: hasUploadedMemory,
        action: onUploadMemory,
        cta: 'Upload Memory',
        Icon: ImagePlus,
      },
      {
        key: 'invited',
        title: 'Invite your family',
        body: 'They can claim their profile and add their own memories.',
        done: hasInvitedFamily,
        action: onInviteFamily,
        cta: 'Invite Family',
        Icon: Mail,
      },
    ],
    [
      hasAddedFamilyMember,
      hasUploadedMemory,
      hasInvitedFamily,
      onAddMember,
      onUploadMemory,
      onInviteFamily,
    ],
  );

  // The "next" step gets the spotlight ring.
  const nextIndex = steps.findIndex((s) => !s.done);
  const doneCount = steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);

  // Don't render anything once everything's done.
  if (allDone) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent className="max-w-md p-0 overflow-hidden bg-white border-fuchsia-200">
          <div className="bg-gradient-to-br from-fuchsia-600 via-pink-500 to-amber-400 px-5 py-5 text-white">
            <DialogHeader className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                  <Rocket className="w-5 h-5" />
                </div>
                <DialogTitle className="font-serif text-xl text-white">
                  Let's build your family in 60 seconds
                </DialogTitle>
              </div>
              <DialogDescription className="text-white/85 text-sm">
                Three quick steps to bring KinsCircle to life. You can skip
                any time — nothing here is required.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-3">
              <div className="h-1.5 w-full bg-white/25 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="text-[11px] mt-1 text-white/85">
                {doneCount} of {steps.length} done
              </div>
            </div>
          </div>

          <div className="px-4 py-4 space-y-2.5">
            {steps.map((s, i) => {
              const isNext = i === nextIndex;
              const Icon = s.Icon;
              return (
                <div
                  key={s.key}
                  className={`relative rounded-xl border p-3 flex items-start gap-3 transition ${
                    s.done
                      ? 'border-emerald-200 bg-emerald-50/50'
                      : isNext
                        ? 'border-fuchsia-300 bg-fuchsia-50/40 ring-2 ring-fuchsia-200'
                        : 'border-slate-200 bg-white'
                  }`}
                >
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                      s.done
                        ? 'bg-emerald-500 text-white'
                        : isNext
                          ? 'bg-gradient-to-br from-fuchsia-500 to-pink-500 text-white'
                          : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {s.done ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className={`text-sm font-semibold leading-tight ${
                        s.done ? 'text-emerald-800' : 'text-slate-900'
                      }`}
                    >
                      Step {i + 1}: {s.title}
                    </div>
                    <div
                      className={`text-xs mt-0.5 ${
                        s.done ? 'text-emerald-700/80' : 'text-slate-600'
                      }`}
                    >
                      {s.body}
                    </div>
                    {!s.done && (
                      <Button
                        size="sm"
                        onClick={() => {
                          s.action();
                          // Don't auto-close — user might want to come back
                          // and tick off another step. The X is right there.
                        }}
                        className={`mt-2 h-8 px-3 text-xs gap-1 ${
                          isNext
                            ? 'bg-gradient-to-r from-fuchsia-600 to-pink-500 text-white hover:from-fuchsia-700 hover:to-pink-600'
                            : 'bg-slate-900 hover:bg-slate-800 text-white'
                        }`}
                      >
                        {s.cta}
                        <ArrowRight className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}

            <div className="flex items-center justify-between pt-1">
              {onOpenTour ? (
                <button
                  onClick={() => {
                    onOpenTour();
                  }}
                  className="text-xs text-fuchsia-700 hover:text-fuchsia-800 font-medium inline-flex items-center gap-1"
                >
                  <Sparkles className="w-3 h-3" />
                  Take the full tour
                </button>
              ) : (
                <span />
              )}
              <button
                onClick={close}
                className="text-xs text-slate-500 hover:text-slate-700"
              >
                I'll do this later
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Persistent "Continue Setup" pill — only when overlay is closed
          early and there's still work to do. Stays out of the way and
          mirrors the spec's "non-blocking" requirement. Sits above the
          existing OnboardingChecklist card on mobile. */}
      {!open && dismissed && !allDone && (
        <button
          onClick={reopen}
          className="fixed bottom-4 left-1/2 -translate-x-1/2 sm:left-auto sm:right-4 sm:translate-x-0 z-30 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-lg border border-fuchsia-200 text-fuchsia-700 text-xs font-semibold hover:bg-fuchsia-50"
          title="Continue Setup"
        >
          <Rocket className="w-3.5 h-3.5" />
          Continue Setup ({doneCount}/{steps.length})
        </button>
      )}
    </>
  );
};

export default ActivationOverlay;

/* ───────────── Smart activation nudges (banner) ─────────────
 * Tiny stateless banner the dashboard can render in empty states to
 * naturally guide the user to the next best action. Reads activation
 * status only — never writes anything. Safe to mount alongside the
 * existing SmartSuggestions panel; it does not duplicate suggestions,
 * it just surfaces the SAME activation funnel in-context.
 */
export interface ActivationNudgeProps {
  completed: { added: boolean; uploaded: boolean; invited: boolean };
  peopleCount: number;
  mediaCount: number;
  onAddMember: () => void;
  onUploadMemory: () => void;
  onInviteFamily: () => void;
  className?: string;
}

export const ActivationNudge: React.FC<ActivationNudgeProps> = ({
  completed,
  peopleCount,
  mediaCount,
  onAddMember,
  onUploadMemory,
  onInviteFamily,
  className = '',
}) => {
  const [closed, setClosed] = useState(false);

  const hasAddedFamilyMember = completed.added || peopleCount > 1;
  const hasUploadedMemory = completed.uploaded || mediaCount > 0;
  const hasInvitedFamily = completed.invited;

  // Pick the highest-priority unfinished activation step to surface.
  let line: { msg: string; cta: string; action: () => void } | null = null;
  if (!hasAddedFamilyMember) {
    line = {
      msg: 'Add a parent to start building your family tree',
      cta: 'Add Family Member',
      action: onAddMember,
    };
  } else if (!hasUploadedMemory) {
    line = {
      msg: 'Add your first memory to bring your timeline to life',
      cta: 'Upload Memory',
      action: onUploadMemory,
    };
  } else if (!hasInvitedFamily) {
    line = {
      msg: 'Invite family so they can add their memories',
      cta: 'Invite Family',
      action: onInviteFamily,
    };
  }

  if (!line || closed) return null;

  return (
    <div
      className={`mx-auto max-w-3xl px-4 ${className}`}
      role="status"
      aria-live="polite"
    >
      <div className="rounded-2xl border border-fuchsia-200 bg-gradient-to-r from-fuchsia-50 via-pink-50 to-amber-50 p-3 sm:p-4 flex items-center gap-3 shadow-sm">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center text-white shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 leading-tight">
            {line.msg}
          </div>
          <div className="text-[11px] text-slate-600 mt-0.5">
            One small step makes everything else easier.
          </div>
        </div>
        <Button
          size="sm"
          onClick={line.action}
          className="bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white text-xs h-8 px-3 shrink-0"
        >
          {line.cta}
        </Button>
        <button
          onClick={() => setClosed(true)}
          aria-label="Dismiss"
          className="p-1 rounded-full hover:bg-white/70 text-slate-500 shrink-0"
        >
          <XIcon className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
